import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTopBar } from '@/contexts/TopBarContext'
import {
  Plus, CalendarDays, Pencil, Trash2, Eye, Code2, Search, X, Globe, ExternalLink, UserCheck, Clock,
  ChevronUp, ChevronDown, ChevronsUpDown,
} from 'lucide-react'
import { adminApi } from '@/api/admin'
import type { CourseSessionDto } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { ActionsMenu } from '@/components/shared/ActionsMenu'
import { TablePagination } from '@/components/shared/TablePagination'
import { usePagination } from '@/hooks/usePagination'
import SessionFormModal from './SessionFormModal'
import EmbedSnippetModal from './EmbedSnippetModal'
import { sessionStatusUpdate } from './sessionUpdate'
import ProUpgradeDialog from '@/components/shared/ProUpgradeDialog'
import { formatPriceLabel } from '@/lib/pricing'
import { useAdminMoney } from '@/hooks/useMoney'
import { useAdminDates } from '@/hooks/useDates'

type Status = CourseSessionDto['status']
const STATUSES: Status[] = ['Draft', 'Published', 'Completed', 'Cancelled']

const STATUS_COLOUR: Record<Status, string> = {
  Draft:     'text-slate-600 bg-slate-100 border-slate-200',
  Published: 'text-green-700 bg-green-50 border-green-200',
  Completed: 'text-blue-700 bg-blue-50 border-blue-200',
  Cancelled: 'text-red-600 bg-red-50 border-red-200',
}

type SortKey = 'startsAt' | 'courseName' | 'bookedPlaces' | 'status'
type SortDir = 'asc' | 'desc'

function SortIcon({ col, sortKey, dir }: { col: SortKey; sortKey: SortKey; dir: SortDir }) {
  if (col !== sortKey) return <ChevronsUpDown size={13} className="text-gray-300 ml-1 inline" />
  return dir === 'asc'
    ? <ChevronUp size={13} className="text-indigo-500 ml-1 inline" />
    : <ChevronDown size={13} className="text-indigo-500 ml-1 inline" />
}

function Th({ label, col, sortKey, dir, onSort }: {
  label: string; col: SortKey; sortKey: SortKey; dir: SortDir
  onSort: (col: SortKey) => void
}) {
  return (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
      onClick={() => onSort(col)}
    >
      {label}<SortIcon col={col} sortKey={sortKey} dir={dir} />
    </th>
  )
}

export default function SessionsPage() {
  const { formatDate, formatTime } = useAdminDates()
  const fmtDate = (iso: string) => formatDate(iso, { day: 'numeric', month: 'short', year: 'numeric' })
  const fmtTime = (iso: string) => formatTime(iso)
  const { fmt: fmtMoney } = useAdminMoney()
  useTopBar(<span className="text-sm font-medium text-gray-900">Public Sessions</span>)
  const qc = useQueryClient()
  const navigate = useNavigate()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<CourseSessionDto | null>(null)
  const [showEmbed, setShowEmbed] = useState(false)
  const [showUpgrade, setShowUpgrade] = useState(false)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<Status | ''>('')
  const [sortKey, setSortKey] = useState<SortKey>('startsAt')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })
  const isPro = profile?.planTier === 'Pro'

  // Everything is fetched once and filtered here, as the bookings list does — so the status pills, search and
  // the count above the table all agree.
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => adminApi.getSessions().then(r => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteSession(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
    onError: () => alert('Failed to delete the session.'),
  })

  const publishMutation = useMutation({
    mutationFn: (s: CourseSessionDto) => adminApi.updateSession(s.id, sessionStatusUpdate(s, 'Published')),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
    onError: () => alert('Failed to publish the session.'),
  })

  const handleSort = (col: SortKey) => {
    if (sortKey === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(col); setSortDir('desc') }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return sessions
      .filter(s => !statusFilter || s.status === statusFilter)
      .filter(s => !q || [
        s.courseName,
        s.venue?.name ?? '', s.venue?.city ?? '',
        ...(s.trainerAssignments ?? []).map(t => `${t.firstName} ${t.lastName}`),
      ].some(v => v.toLowerCase().includes(q)))
      .sort((a, b) => {
        let cmp = 0
        switch (sortKey) {
          case 'startsAt':      cmp = Date.parse(a.startsAt) - Date.parse(b.startsAt); break
          case 'courseName':    cmp = a.courseName.localeCompare(b.courseName); break
          case 'bookedPlaces':  cmp = a.bookedPlaces - b.bookedPlaces; break
          case 'status':        cmp = a.status.localeCompare(b.status); break
        }
        return sortDir === 'asc' ? cmp : -cmp
      })
  }, [sessions, search, statusFilter, sortKey, sortDir])

  const { paged, page, setPage, totalPages, total, pageSize, start } = usePagination(filtered)

  const publicUrl = (s: CourseSessionDto) =>
    profile?.slug ? `${window.location.origin}/sessions/${profile.slug}/${s.id}` : null

  const openSession = (s: CourseSessionDto) => navigate(`/admin/sessions/${s.id}`)

  const venueLabel = (s: CourseSessionDto) =>
    s.deliveryMethod === 'Online' ? 'Online'
    : s.venue ? `${s.venue.name}${s.venue.city ? `, ${s.venue.city}` : ''}`
    : null

  const priceLabel = (s: CourseSessionDto) =>
    formatPriceLabel(s.pricingModel, s.price, s.flatFeeAmount, s.flatFeeCapacity, fmtMoney)

  const leadLabel = (s: CourseSessionDto) => {
    const trainers = s.trainerAssignments ?? []
    const lead = trainers.find(t => t.role === 'Lead')
    return { lead: lead ? `${lead.firstName} ${lead.lastName}` : null, count: trainers.length }
  }

  const actionsFor = (s: CourseSessionDto) => {
    const url = publicUrl(s)
    return [
      { label: 'Open session', icon: <Eye size={14} className="text-gray-500" />, onClick: () => openSession(s) },
      { label: 'Edit', icon: <Pencil size={14} className="text-gray-500" />, onClick: () => setEditing(s) },
      ...(s.status === 'Draft' ? [{
        label: 'Publish',
        icon: <Globe size={14} className="text-green-600" />,
        onClick: () => publishMutation.mutate(s),
        disabled: publishMutation.isPending,
      }] : []),
      ...(s.status === 'Published' && url ? [{
        label: 'View public page',
        icon: <ExternalLink size={14} className="text-gray-500" />,
        onClick: () => { window.open(url, '_blank', 'noopener,noreferrer') },
      }] : []),
      {
        label: 'Delete',
        icon: <Trash2 size={14} className="text-red-600" />,
        onClick: () => {
          if (confirm('Delete this session? This cannot be undone.')) deleteMutation.mutate(s.id)
        },
        disabled: deleteMutation.isPending,
        danger: true,
      },
    ]
  }

  const emptyMessage = sessions.length === 0 ? 'No sessions yet.' : 'No sessions match your filters.'

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <p className="text-sm text-gray-500">Scheduled courses anyone can book onto.</p>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-400">{filtered.length} of {sessions.length}</div>
          <Button size="sm" variant="outline" onClick={() => isPro ? setShowEmbed(true) : setShowUpgrade(true)}>
            <Code2 size={15} className="mr-1.5" /> Embed
            {!isPro && <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">Pro</span>}
          </Button>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus size={15} className="mr-1.5" /> New public session
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-56">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search course, venue, trainer…"
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
              <X size={14} />
            </button>
          )}
        </div>

        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setStatusFilter('')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border cursor-pointer transition-colors ${
              statusFilter === '' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
            }`}
          >
            All
          </button>
          {STATUSES.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border cursor-pointer transition-colors ${
                statusFilter === s ? STATUS_COLOUR[s] + ' font-semibold' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {isLoading && <p className="text-center text-gray-500 py-8">Loading…</p>}
        {!isLoading && filtered.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400">{emptyMessage}</p>
            {sessions.length === 0 && (
              <Button className="mt-4" onClick={() => setCreating(true)}>
                <Plus size={15} className="mr-1" /> Create your first session
              </Button>
            )}
          </div>
        )}
        {paged.map(s => {
          const { lead, count } = leadLabel(s)
          const venue = venueLabel(s)
          const price = priceLabel(s)
          return (
            <div
              key={s.id}
              onClick={() => openSession(s)}
              className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-indigo-200"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate">{s.courseName}</p>
                  <p className="text-sm text-gray-500 truncate">{venue ?? 'Venue to be confirmed'}</p>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${STATUS_COLOUR[s.status]}`}>
                  {s.status}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                <span>
                  {fmtDate(s.startsAt)}
                  {(s.additionalDays?.length ?? 0) > 0 && <span className="ml-1 text-gray-400">+{s.additionalDays.length}d</span>}
                </span>
                <span>{fmtTime(s.startsAt)}{s.endsAt && ` – ${fmtTime(s.endsAt)}`}</span>
                <span className={s.availablePlaces === 0 ? 'text-amber-700 font-medium' : ''}>{s.bookedPlaces} / {s.totalPlaces} booked</span>
                {lead ? <span>{lead}</span> : count > 0 ? <span className="text-amber-700">No lead trainer</span> : <span className="text-gray-400">Trainer TBC</span>}
                <span>{price ?? 'No price set'}</span>
              </div>
            </div>
          )
        })}
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th label="Date"     col="startsAt"     sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Course"   col="courseName"   sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Venue</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Lead trainer</th>
                <Th label="Booked"   col="bookedPlaces" sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Price</th>
                <Th label="Status"   col="status"       sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr><td colSpan={8} className="text-center text-gray-500 py-12">Loading…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center text-gray-400 py-12">
                  <CalendarDays size={28} className="mx-auto text-gray-300 mb-2" />
                  {emptyMessage}
                  {sessions.length === 0 && (
                    <div>
                      <Button className="mt-4" onClick={() => setCreating(true)}>
                        <Plus size={15} className="mr-1" /> Create your first session
                      </Button>
                    </div>
                  )}
                </td></tr>
              )}
              {paged.map(s => {
                const { lead, count } = leadLabel(s)
                const venue = venueLabel(s)
                const price = priceLabel(s)
                const full = s.availablePlaces === 0
                return (
                  <tr
                    key={s.id}
                    className="hover:bg-indigo-50/40 cursor-pointer transition-colors"
                    onClick={() => openSession(s)}
                  >
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {fmtDate(s.startsAt)}
                      {(s.additionalDays?.length ?? 0) > 0 && (
                        <span className="ml-1.5 text-xs text-gray-400">+{s.additionalDays.length}d</span>
                      )}
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock size={11} />{fmtTime(s.startsAt)}{s.endsAt && ` – ${fmtTime(s.endsAt)}`}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{s.courseName}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {venue ?? <span className="text-gray-400">To be confirmed</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {lead ? (
                        <span className="flex items-center gap-1 text-gray-600">
                          <UserCheck size={13} className="text-gray-400 shrink-0" />
                          {lead}
                          {count > 1 && <span className="text-xs text-gray-400">+{count - 1}</span>}
                        </span>
                      ) : count > 0 ? (
                        <span className="text-amber-700" title="No lead trainer assigned">No lead trainer</span>
                      ) : (
                        <span className="text-gray-400">Trainer TBC</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={full ? 'text-amber-700 font-medium' : 'text-gray-600'}>
                        {s.bookedPlaces} / {s.totalPlaces}
                      </span>
                      {full && <span className="ml-1.5 text-xs text-amber-600">Full</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {price ?? <span className="text-gray-400">No price set</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLOUR[s.status]}`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <ActionsMenu items={actionsFor(s)} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>

      {showEmbed && <EmbedSnippetModal onClose={() => setShowEmbed(false)} />}
      <ProUpgradeDialog
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        featureName="Website embed widget"
      />

      {(creating || editing) && (
        <SessionFormModal
          session={editing}
          onClose={() => { setCreating(false); setEditing(null) }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            qc.invalidateQueries({ queryKey: ['sessions'] })
          }}
        />
      )}
    </div>
  )
}
